import type {User} from '@perfectpost/perfect-post-common';

import type {MixPanelService} from './mixpanel.service';

/**
 * Émetteur de secours, utilisé quand `mixpanel-browser` n'a pas pu être chargé — un bloqueur de
 * publicité refuse le module dès que son URL contient « mixpanel ». Avant, tous les événements de
 * la session étaient jetés en silence.
 *
 * ⚠️ Ni ce fichier ni son import ne doivent contenir « mixpanel » dans un chemin chargé par le
 * navigateur : il est importé STATIQUEMENT par le provider (aucun chunk à part) précisément pour
 * ne pas être refusé à son tour. `import type` seulement depuis `mixpanel.service`.
 *
 * Il parle directement l'API d'ingestion de Mixpanel (même format que l'extension), mais via le
 * relais first-party de l'API (`/public/mp/`, que les listes de blocage ne visent pas). Identité :
 * même règle que la bibliothèque — un appareil anonyme `$device:<uuid>` gardé en stockage local,
 * puis l'`_id` Mongo à la connexion, avec un `$identify` pour fusionner (docs/mixpanel-identite.md).
 */

type MixpanelClient = Pick<MixPanelService, 'track' | 'identifyUser' | 'logout' | 'setPageContext'>;
type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem'>;
type Sender = (url: string, body: string) => void;

interface FallbackIdentity {
  deviceId: string;
  userId?: string;
}

const STORAGE_KEY = 'pp:mp-fallback';

const defaultStorage = (): Storage | undefined => {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

const defaultSender: Sender = (url, body) => {
  // `keepalive` : l'envoi survit à une navigation ou à la fermeture de l'onglet.
  void fetch(url, {
    method: 'POST',
    headers: {'Content-Type': 'application/x-www-form-urlencoded'},
    body,
    keepalive: true,
  }).catch(() => undefined);
};

const toBase64 = (json: string): string => {
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
};

const randomId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

/** Valeurs standard de Mixpanel, que la bibliothèque calcule d'habitude elle-même. */
export function inferBrowser(userAgent: string): string | undefined {
  if (/Firefox\//.test(userAgent)) return 'Firefox';
  if (/Edg\//.test(userAgent)) return 'Microsoft Edge';
  if (/OPR\//.test(userAgent)) return 'Opera';
  if (/Chrome\//.test(userAgent)) return 'Chrome';
  if (/Safari\//.test(userAgent)) return 'Safari';
  return undefined;
}

export function inferOs(userAgent: string): string | undefined {
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'iOS';
  if (/Android/.test(userAgent)) return 'Android';
  if (/Mac OS X|Macintosh/.test(userAgent)) return 'Mac OS X';
  if (/Windows/.test(userAgent)) return 'Windows';
  if (/CrOS/.test(userAgent)) return 'Chrome OS';
  if (/Linux/.test(userAgent)) return 'Linux';
  return undefined;
}

export class FallbackEmitter implements MixpanelClient {
  private identity: FallbackIdentity;
  private superProps: Record<string, unknown> = {};

  constructor(
    private readonly token: string,
    apiEndpoint: string,
    private readonly storage: Storage | undefined = defaultStorage(),
    private readonly sender: Sender = defaultSender,
  ) {
    this.trackUrl = `${apiEndpoint.replace(/\/+$/, '')}/public/mp/track/?ip=1`;
    this.identity = this.load() ?? this.persist({deviceId: randomId()});
    // Équivalent de `track_pageview: true` de la bibliothèque : une page vue au chargement.
    this.track('$mp_web_page_view');
  }

  private readonly trackUrl: string;

  /** Le contexte de page est relu à chaque envoi : rien à mémoriser. */
  public setPageContext(): void {}

  public logout(): void {
    this.superProps = {};
    this.identity = this.persist({deviceId: randomId()});
  }

  public identifyUser(user: User, overrides?: Parameters<MixPanelService['identifyUser']>[1]): void {
    const userId = String(user._id);
    const previousUserId = this.identity.userId;
    if (previousUserId !== userId) {
      this.identity = this.persist({...this.identity, userId});
      // Pas de fusion croisée sur un changement de compte sans logout (même règle que l'extension).
      if (previousUserId === undefined) {
        const anonymousId = `$device:${this.identity.deviceId}`;
        this.track('$identify', {$identified_id: userId, $anon_id: anonymousId, $anon_distinct_id: anonymousId});
      }
    }
    this.superProps = {
      ...this.superProps,
      ...(overrides?.['Account Status'] !== undefined ? {'Account Status': overrides['Account Status']} : {}),
      ...(overrides?.Platform !== undefined ? {Platform: overrides.Platform} : {}),
      ...(overrides?.['Company Name'] !== undefined ? {'Company Name': overrides['Company Name']} : {}),
      ...(overrides?.['Company Size'] !== undefined ? {'Company Size': overrides['Company Size']} : {}),
    };
  }

  public track(eventName: string, props?: Record<string, unknown>): void {
    try {
      const {deviceId, userId} = this.identity;
      const userAgent = navigator.userAgent;
      const browser = inferBrowser(userAgent);
      const os = inferOs(userAgent);
      const event = {
        event: eventName,
        properties: {
          token: this.token,
          distinct_id: userId ?? `$device:${deviceId}`,
          $device_id: deviceId,
          ...(userId !== undefined ? {$user_id: userId} : {}),
          time: Date.now(),
          $insert_id: randomId(),
          mp_lib: 'web-fallback',
          ...(browser !== undefined ? {$browser: browser} : {}),
          ...(os !== undefined ? {$os: os} : {}),
          $current_url: window.location.href,
          $referrer: document.referrer || '$direct',
          application: 'web',
          ...this.superProps,
          ...props,
        },
      };
      this.sender(this.trackUrl, `data=${encodeURIComponent(toBase64(JSON.stringify([event])))}`);
    } catch {
      // Mesure best-effort : jamais d'erreur dans l'app pour elle.
    }
  }

  private load(): FallbackIdentity | undefined {
    try {
      const raw = this.storage?.getItem(STORAGE_KEY);
      if (raw === null || raw === undefined) return undefined;
      const parsed = JSON.parse(raw) as Partial<FallbackIdentity>;
      return typeof parsed.deviceId === 'string' ? {deviceId: parsed.deviceId, userId: parsed.userId} : undefined;
    } catch {
      return undefined;
    }
  }

  private persist(identity: FallbackIdentity): FallbackIdentity {
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(identity));
    } catch {
      // Stockage indisponible : identité limitée à la session.
    }
    return identity;
  }
}
